import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import ForceGraph2D from "react-force-graph-2d";
import api from "../lib/api";
import { SECTOR_COLORS } from "../lib/format";

export default function SynergyMap() {
  const [graph, setGraph] = useState({ nodes: [], links: [] });
  const [filter, setFilter] = useState("All");
  const [hovered, setHovered] = useState(null);
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 560 });

  useEffect(() => {
    document.title = "Synergy Map · The Synergy Table";
    api.get("/synergy/graph").then((r) => setGraph(r.data));
  }, []);

  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      setSize({ w: Math.max(320, r.width), h: Math.max(420, Math.min(720, window.innerHeight - 240)) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const sectors = ["All", ...Array.from(new Set(graph.nodes.map((n) => n.sector).filter(Boolean)))];

  const filteredNodes = filter === "All" ? graph.nodes : graph.nodes.filter((n) => n.sector === filter);
  const filteredIds = new Set(filteredNodes.map((n) => n.id));
  const filteredLinks = graph.links.filter((l) => filteredIds.has(l.source.id || l.source) && filteredIds.has(l.target.id || l.target));

  // Stable graphData reference — prevents react-force-graph from resetting the
  // d3 simulation on every parent rerender (e.g. hover state changes).
  const graphData = useMemo(
    () => ({ nodes: filteredNodes, links: filteredLinks }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, filter]
  );

  const nodeColor = (n) => SECTOR_COLORS[n.sector] || n.avatar_color || "#C9A84C";

  const paintNode = useCallback((node, ctx, scale) => {
    const r = 9 + (node.degree || 1) * 0.5;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = nodeColor(node);
    ctx.shadowColor = nodeColor(node);
    ctx.shadowBlur = 12;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "#0D0D0D";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    const fontSize = 12 / scale;
    ctx.font = `${fontSize}px Outfit`;
    ctx.fillStyle = "#F0EDE8";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(node.name, node.x, node.y + r + 4);
    ctx.font = `${fontSize * 0.78}px Outfit`;
    ctx.fillStyle = "#9A9590";
    ctx.fillText(node.sector, node.x, node.y + r + 4 + fontSize + 2);
  }, []);

  return (
    <div className="space-y-8" data-testid="synergy-map-page">
      <div>
        <div className="overline">Map</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-2 tracking-tight">Synergy Map</h1>
        <p className="text-[#B8B3AC] mt-3 max-w-xl">
          Lines show members who already closed at least one deal together. Filter by sector to find complementary pairs for a new project.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="synergy-filter">
        {sectors.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest transition ${
              filter === s ? "bg-[#C9A84C] text-[#0D0D0D]" : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55 hover:text-[#C9A84C]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div ref={wrapRef} className="ts-card overflow-hidden p-0 relative" style={{ minHeight: 560 }}>
        <ForceGraph2D
          graphData={graphData}
          width={size.w}
          height={size.h}
          backgroundColor="#0D0D0D"
          linkColor={() => "rgba(201,168,76,0.35)"}
          linkWidth={(l) => Math.min(3, (l.weight || 1))}
          nodeRelSize={6}
          nodeCanvasObject={paintNode}
          nodePointerAreaPaint={(node, color, ctx) => {
            ctx.beginPath();
            ctx.arc(node.x, node.y, 12, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }}
          onNodeHover={(n) => setHovered(n)}
          onNodeClick={(n) => { window.location.href = `/members/${n.id}`; }}
          cooldownTicks={120}
          d3VelocityDecay={0.35}
          linkDirectionalParticles={1}
          linkDirectionalParticleSpeed={0.005}
          linkDirectionalParticleWidth={2}
          linkDirectionalParticleColor={() => "#C9A84C"}
        />
        {hovered && (
          <div className="absolute top-4 left-4 ts-card px-4 py-3 text-xs">
            <div className="font-serif text-base text-[#F0EDE8]">{hovered.name}</div>
            <div className="text-[#C9A84C] uppercase tracking-widest mt-0.5">{hovered.sector}</div>
            <Link to={`/members/${hovered.id}`} className="text-[#9A9590] hover:text-[#C9A84C] mt-1 block">Open profile →</Link>
          </div>
        )}
      </div>

      <div className="ts-card p-6 text-xs text-[#9A9590] flex flex-wrap gap-4">
        {Object.entries(SECTOR_COLORS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: v }} /> {k}
          </div>
        ))}
      </div>
    </div>
  );
}
